import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifyBoardSync, refreshBoard } from "./useBoardEvents";
import { type ScenarioSummary } from "./api";
import { BoardResetButton } from "./BoardResetButton";
import { ReplayTimeline } from "./ReplayTimeline";
import { useReplay } from "./ReplayContext";
import { useReplayPlayback } from "./useReplayPlayback";
import { useApi } from "./useApi";

export function SimSidebar() {
  const api = useApi("sim");
  const queryClient = useQueryClient();
  const { selected, selectScenario, stepStates, playing, resetTimeline, replayOutcome } = useReplay();

  const scenarios = useQuery({
    queryKey: ["sim-scenarios"],
    queryFn: api.simScenarios,
  });
  const status = useQuery({ queryKey: ["sim-status"], queryFn: api.simStatus });
  const { play } = useReplayPlayback();
  const [liveCloneNotice, setLiveCloneNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!selected && scenarios.data?.length) {
      selectScenario(scenarios.data[0]);
    }
  }, [selected, scenarios.data, selectScenario]);

  const refresh = useMutation({
    mutationFn: api.simRefresh,
    onSuccess: (data) => {
      resetTimeline();
      notifyBoardSync("sim");
      refreshBoard(queryClient, "sim", { includeAdvisory: true });
      void queryClient.refetchQueries({ queryKey: ["sim-status"] });
      const warning =
        typeof data === "object" && data !== null && "warning" in data
          ? String((data as { warning?: string }).warning ?? "")
          : "";
      if (warning) {
        setLiveCloneNotice(warning);
      }
    },
  });

  const onRefreshFromLive = () => {
    const ok = window.confirm(
      "Copy today's LIVE board into sim?\n\n" +
        "Use this when you want to poke at the current live picture in sim. " +
        "For the 4 Jan mishap demo, use Play replay instead (wipes sim and applies scenario YAML).",
    );
    if (ok) {
      refresh.mutate();
    }
  };

  const onSelect = (scenario: ScenarioSummary) => {
    if (playing) return;
    selectScenario(scenario);
    resetTimeline();
  };

  const done = stepStates.length > 0 && !playing && !play.isPending;
  const played = stepStates.filter((s) => s === "ok" || s === "active").length;

  return (
    <aside className="sim-sidebar" aria-label="Simulation control">
      <header className="sim-sidebar-head">
        <h2>Replay</h2>
        <p className="muted">
          Loaded{" "}
          <span className="status-pill">{status.data?.loaded_scenario ?? "none"}</span>
        </p>
      </header>

      <div className="sim-sidebar-actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onRefreshFromLive}
          disabled={refresh.isPending || playing}
          title="Copy live board into sim"
        >
          {refresh.isPending ? "…" : "From live"}
        </button>
        <BoardResetButton env="sim" compact />
      </div>

      {liveCloneNotice ? (
        <p className="warn sim-live-clone-warn" role="status">
          {liveCloneNotice}
        </p>
      ) : null}

      <p className="muted sim-replay-hint">
        Two modes: <strong>From live</strong> copies today&apos;s live board into sim.{" "}
        <strong>Play replay</strong> wipes sim and steps through the scenario (4 Jan facts only).
      </p>

      {refresh.isError ? (
        <div className="error">{(refresh.error as Error).message}</div>
      ) : null}

      {scenarios.isLoading ? <p className="muted">Loading scenarios…</p> : null}
      {scenarios.error ? (
        <div className="error">{(scenarios.error as Error).message}</div>
      ) : null}

      <div className="scenario-list scenario-list--sidebar">
        {(scenarios.data ?? []).map((s) => (
          <button
            key={s.path}
            type="button"
            className={`scenario-card${selected?.path === s.path ? " scenario-card--selected" : ""}`}
            onClick={() => onSelect(s)}
            disabled={playing}
          >
            <h3>{s.name}</h3>
            {s.description ? <p>{s.description}</p> : null}
            <span className="scenario-meta muted">{s.step_count} steps</span>
          </button>
        ))}
      </div>

      {selected && (selected.steps?.length ?? 0) > 0 ? <ReplayTimeline /> : null}

      <button
        type="button"
        className="btn sim-play-btn"
        onClick={() => {
          setLiveCloneNotice(null);
          play.mutate();
        }}
        disabled={play.isPending || playing || !selected}
      >
        {playing ? "Playing…" : "Play replay"}
      </button>

      {play.isError ? <div className="error">{(play.error as Error).message}</div> : null}
      {done && replayOutcome ? (
        <div className="replay-outcome" aria-live="polite">
          {replayOutcome.conflict_scan_error ? (
            <p className="error replay-outcome-line">
              ConflictFinder failed: {replayOutcome.conflict_scan_error}
            </p>
          ) : replayOutcome.blocking_conflict_count > 0 ? (
            <p className="replay-outcome-line replay-outcome-line--blocked">
              ConflictFinder blocked the brief: {replayOutcome.blocking_conflict_count}{" "}
              mismatch{replayOutcome.blocking_conflict_count === 1 ? "" : "es"}.
              {replayOutcome.blocking_conflicts?.[0]?.message
                ? ` ${replayOutcome.blocking_conflicts[0].message}`
                : ""}
            </p>
          ) : replayOutcome.brief_state === "blocked" ? (
            <p className="replay-outcome-line replay-outcome-line--blocked">
              Brief recorded as blocked.
            </p>
          ) : (
            <p className="warn replay-outcome-line">
              Replay finished with no blocking conflicts. ConflictFinder did not flag a
              mismatch (check API key, model, or open Dashboard).
            </p>
          )}
        </div>
      ) : done && played > 0 ? (
        <p className="muted replay-done-hint">
          Replay complete. Open Dashboard or Brief to review ConflictFinder output.
        </p>
      ) : null}
    </aside>
  );
}
